import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Tooltip, Typography, Checkbox, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  formattedJson,
  getFormattedDateTime,
  removeCheckboxFromJson,
} from "../../services/utils";
import { useParams } from "react-router-dom";
import useFetchDomainBackups from "../../hooks/useFetchDomainBackups";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import AuditComponent from "../../components/AuditComponent";
import useFetchDomainById from "../../hooks/useFetchDomainById";
import { IconCompareReportSvg, IconReportsSvg } from "../../common/utils/icons";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { getConfig } from "../../config/runtimeConfig";

const AuditDomain = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { id }: any = useParams();
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const defaultPageSize = getConfig("REACT_APP_DEFAULT_PAGE_SIZE", "25");
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(
    defaultPageSize ? parseInt(defaultPageSize) : 25,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [auditSource, setAuditSource] = useState("");
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedInfoData, setSelectedInfoData] = useState<any>(null);

  const [domainDataState, setDomainDataState] = useState({});
  const domainDataRef = useRef<any>({});
  const [fetchedDomainsData] = useFetchDomainById({
    setIsLoading,
    currentDomainId: id,
  });

  const [resourceBackupData] = useFetchDomainBackups({
    domainId: id,
    setIsLoading,
    page,
    pSize,
  });
  useEffect(() => {
    setDomainDataState(fetchedDomainsData || {});
    domainDataRef.current = fetchedDomainsData || {};
  }, [fetchedDomainsData]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        flex: 0,
        minWidth: 0,
        renderCell: (params: any) => null,
      },
      {
        field: "checkbox",
        headerName: "",
        width: 80,
        renderCell: (params) => {
          const isSelected = selectedRows.some(
            (row) => row.id === params?.row?.id,
          );
          return (
            <Box sx={{ paddingTop: "4px" }}>
              <Checkbox
                checked={isSelected}
                onChange={() => toggleSelection(params?.row)}
                sx={{
                  "&.Mui-checked": {
                    color: "#FFA500",
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "domain_code",
        headerName: "Code",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "created_date",
        headerName: "Last Modified",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          return getFormattedDateTime(params?.value);
        },
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value?.username} />;
        },
      },
      {
        field: "comment",
        headerName: "Comments",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
          return <LimitChractersWithTooltip value={params?.value || ""} />;
        },
      },
      {
        field: "info",
        headerName: "Domain Info",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => {
          const { checkbox, ...rowWithoutCheckbox } = params.row;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <React.Fragment>
                      <Typography color="inherit">Domain Info</Typography>

                      <Typography>
                        {formattedJson(JSON.stringify(rowWithoutCheckbox))}
                      </Typography>
                    </React.Fragment>
                  </pre>
                }
              >
                <InfoIcon
                  sx={{
                    width: "16px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
              <Tooltip title="Expand to view full details" placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedInfoData(rowWithoutCheckbox);
                    setOpenInfoDialog(true);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(255, 165, 0, 0.1)",
                    },
                  }}
                >
                  <OpenInFullIcon sx={{ width: "16px", height: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 130,
        renderCell: (params: any) => {
          return (
            <>
              <Tooltip title="Compare Current Definition" placement="top">
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  disabled={
                    !domainDataRef.current ||
                    Object.keys(domainDataRef.current).length === 0
                  }
                  onClick={() =>
                    compareCurrentItem(params?.row, "currentCompare")
                  }
                >
                  <IconCompareReportSvg />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [selectedRows],
  );

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: fetchedDomainsData?.domain_name,
      id: fetchedDomainsData?.id,
      url: `/domain/view/${id}`,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null, url: "" });
    };
  }, [fetchedDomainsData]);

  const compareCurrentItem = async (data: any, source: any) => {
    const currentResource = domainDataRef.current;
    if (!currentResource || Object.keys(currentResource).length === 0) return; // or show a toast
    const newData = await removeCheckboxFromJson(data);
    // Always replace with exactly two items: selected backup and current resource
    setSelectedRows([newData, currentResource]);
    setAuditSource(source);
    setOpenDialog(true);
  };
  useEffect(() => {
    domainDataRef.current = domainDataState;
  }, [domainDataState]);

  const toggleSelection = async (row: { id: any }) => {
    const newRow = await removeCheckboxFromJson(row);
    setSelectedRows((prevSelectedRows) => {
      if (
        prevSelectedRows.some((selectedRow) => selectedRow.id === newRow.id)
      ) {
        // Deselect row
        return prevSelectedRows.filter(
          (selectedRow) => selectedRow.id !== newRow.id,
        );
      } else {
        // Select row
        if (prevSelectedRows.length < 2) {
          return [...prevSelectedRows, newRow];
        } else {
          // Replace the second selected row with the new row
          return [prevSelectedRows[0], newRow];
        }
      }
    });
  };

  return (
    <>
      <AuditComponent
        auditColumnData={columns || []}
        auditRowData={resourceBackupData || []}
        isLoading={isLoading}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        toggleSelection={toggleSelection}
        dataListTitle="Domain Modification History"
        page={page}
        setPage={setPage}
        pSize={pSize}
        setPSize={setPSize}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        auditSource={auditSource}
        setAuditSource={setAuditSource}
        singlePageMaxHeightDiff={333}
      />
      <ViewPayloadDialog
        openDialog={openInfoDialog}
        dialogTitle="Domain Info"
        payload={selectedInfoData}
        handleDialogClose={() => setOpenInfoDialog(false)}
      />
    </>
  );
};

export default AuditDomain;
